import {
  atom,
  selector,
  useRecoilValue,
  useRecoilTransaction_UNSTABLE,
  GetRecoilValue,
} from "recoil";
import maxBy from "lodash/maxBy";

import { SimpleSetValue } from "../../../../types";
import { QueryStatus, consolidateStatuses } from "../../../../services/query";
import activities from "../../../../redux/activities";

import { getFarmsGlobalStatusModule } from "../../farms/farms.globalStatus";
import { getEnrollmentsGlobalStatusModule } from "../../enrollments/enrollments.globalStatus";
import { getFieldCropsGlobalStatusModule } from "../../fieldCrops/fieldCrops.globalStatus";
import { getFieldsGlobalStatusModule } from "../../fields/fields.globalStatus";
import { getFieldEnrollmentsGlobalStatusModule } from "../../fieldEnrollments/fieldEnrollments.globalStatus";
import { getMembersGlobalStatusModule } from "../../members/members.globalStatus";
import { getInvitesGlobalStatusModule } from "../../invites/invites.globalStatus";

/////////
// START: Block to be removed once deprecated current operation hook is removed.
/////////

// Do NOT export atoms.
// This allows us to limit usage of these to only the hooks we export below.

const dataActivityAtom = atom<activities>({
  key: "[@fabric/core] currentOperationGlobalActivity data",
  default: activities.idling,
});
const dataStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] currentOperationGlobalStatus data",
  default: "idle",
});
const dataFetchStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] currentOperationGlobalFetchStatus data",
  default: "idle",
});
const dataLastUpdatedAtAtom = atom<number>({
  key: "[@fabric/core] currentOperationGlobalLastUpdatedAt data",
  default: Date.now(),
});

const subDataActivityAtom = atom<activities>({
  key: "[@fabric/core] currentOperationGlobalActivity subData",
  default: activities.idling,
});
const subDataStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] currentOperationGlobalStatus subData",
  default: "idle",
});
const subDataFetchStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] currentOperationGlobalFetchStatus subData",
  default: "idle",
});
const subDataLastUpdatedAtAtom = atom<number>({
  key: "[@fabric/core] currentOperationGlobalLastUpdatedAt subData",
  default: Date.now(),
});

const theFetchActivity = activities.setCurrentOperation;

export const getModules = (
  get: GetRecoilValue,
): {
  activity: activities;
  status: QueryStatus;
  fetchStatus: QueryStatus;
  lastUpdatedAt: number;
}[] => [
  {
    activity: get(dataActivityAtom),
    status: get(dataStatusAtom),
    fetchStatus: get(dataFetchStatusAtom),
    lastUpdatedAt: get(dataLastUpdatedAtAtom),
  },
  {
    activity: get(subDataActivityAtom),
    status: get(subDataStatusAtom),
    fetchStatus: get(subDataFetchStatusAtom),
    lastUpdatedAt: get(subDataLastUpdatedAtAtom),
  },
  getFarmsGlobalStatusModule(get),
  getEnrollmentsGlobalStatusModule(get),
  getFieldCropsGlobalStatusModule(get),
  getFieldEnrollmentsGlobalStatusModule(get),
  getFieldsGlobalStatusModule(get),
  getMembersGlobalStatusModule(get),
  getInvitesGlobalStatusModule(get),
];

const currentOperationGlobalActivitySelector = selector<activities>({
  key: "[@fabric/core] currentOperationGlobalActivitySelector",
  get: ({ get }) => {
    const modules = getModules(get);

    // Return whichever is the most recent activity
    const mostRecent = maxBy(modules, "lastUpdatedAt");
    if (!mostRecent) {
      // This should never happen as long as `modules` is not an empty array.
      throw new Error("Could not determine the most recent activity");
    }
    return mostRecent.activity;
  },
});
const currentOperationGlobalStatusSelector = selector<QueryStatus>({
  key: "[@fabric/core] currentOperationGlobalStatusSelector",
  get: ({ get }) => {
    const modules = getModules(get);

    // Return whichever is the most recent status UNLESS the most
    // recent activity is the fetch activity. In that case, we need
    // to consolidate all the fetch statuses and return that.
    const mostRecent = maxBy(modules, "lastUpdatedAt");
    if (!mostRecent) {
      // This should never happen as long as `modules` is not an empty array.
      throw new Error("Could not determine the most recent activity");
    }

    if (mostRecent.activity === theFetchActivity) {
      return consolidateStatuses(...modules.map((x) => x.fetchStatus));
    }
    return mostRecent.status;
  },
});

// #######################################
// ## useCurrentOperationGlobalActivity ##
// #######################################

export type ResultOfUseCurrentOperationGlobalActivity = activities;
export type UseCurrentOperationGlobalActivity =
  () => ResultOfUseCurrentOperationGlobalActivity;

/**
 * @returns The last current operation activity, whether from `useCurrentOperation` or a mutation hook like `useUpdateCurrentOperation`
 *
 * @deprecated Use `useCurrentOperation` or a mutation hook like `useUpdateCurrentOperation` instead. This is only necessary to support deprecated hooks.
 */
export const useCurrentOperationGlobalActivity: UseCurrentOperationGlobalActivity =
  () => {
    return useRecoilValue(currentOperationGlobalActivitySelector);
  };

// #####################################
// ## useCurrentOperationGlobalStatus ##
// #####################################

export type ResultOfUseCurrentOperationGlobalStatus = QueryStatus;
export type UseCurrentOperationGlobalStatus =
  () => ResultOfUseCurrentOperationGlobalStatus;

/**
 * @returns the last status of the last current operation activity, whether from `useCurrentOperation` or a mutation hook like `useUpdateCurrentOperation`.
 *
 * @deprecated Use `useCurrentOperation` or a mutation hook like `useUpdateCurrentOperation` instead. This is only necessary to support deprecated hooks.
 */
export const useCurrentOperationGlobalStatus: UseCurrentOperationGlobalStatus =
  () => {
    return useRecoilValue(currentOperationGlobalStatusSelector);
  };

// #######################################################
// ## useSetCurrentOperationDataGlobalActivityAndStatus ##
// #######################################################

export type ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus =
  SimpleSetValue<{ activity: activities; status: QueryStatus }>;
export type UseSetCurrentOperationDataGlobalActivityAndStatus =
  () => ResultOfUseSetCurrentOperationDataGlobalActivityAndStatus;

/**
 * Allows setting the last current operation data activity.
 * and the status of that activity.
 *
 * @deprecated This is only necessary to support deprecated hooks.
 */
export const useSetCurrentOperationDataGlobalActivityAndStatus: UseSetCurrentOperationDataGlobalActivityAndStatus =
  () => {
    // Using useRecoilTransaction_UNSTABLE ensures that the atoms are updated atomically
    return useRecoilTransaction_UNSTABLE(
      ({ set }) =>
        (payload) => {
          set(dataActivityAtom, payload.activity);
          set(dataStatusAtom, payload.status);
          set(dataLastUpdatedAtAtom, Date.now());

          if (payload.activity === theFetchActivity) {
            set(dataFetchStatusAtom, payload.status);
          }
        },
      [],
    );
  };

// ##########################################################
// ## useSetCurrentOperationSubDataGlobalActivityAndStatus ##
// ##########################################################

export type ResultOfUseSetCurrentOperationSubDataGlobalActivityAndStatus =
  SimpleSetValue<{ activity: activities; status: QueryStatus }>;
export type UseSetCurrentOperationSubDataGlobalActivityAndStatus =
  () => ResultOfUseSetCurrentOperationSubDataGlobalActivityAndStatus;

/**
 * Allows setting the last current operation subdata (fields, farms, etc) activity
 * and the status of that activity.
 *
 * @deprecated This is only necessary to support deprecated hooks.
 */
export const useSetCurrentOperationSubDataGlobalActivityAndStatus: UseSetCurrentOperationSubDataGlobalActivityAndStatus =
  () => {
    // Using useRecoilTransaction_UNSTABLE ensures that the atoms are updated atomically
    return useRecoilTransaction_UNSTABLE(
      ({ set }) =>
        (payload) => {
          set(subDataActivityAtom, payload.activity);
          set(subDataStatusAtom, payload.status);
          set(subDataLastUpdatedAtAtom, Date.now());

          if (payload.activity === theFetchActivity) {
            set(subDataFetchStatusAtom, payload.status);
          }
        },
      [],
    );
  };

///////
// END: Block to be removed once deprecated current operation hook is removed.
///////
